import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
    Key,
    AlertTriangle,
    PhoneOff,
    Clock,
    MessageCircle,
    Send,
    Trash2,
    Phone,
    PhoneCall,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Shield,
    Lock,
    ImagePlus,
    X,
    Download,
    CalendarPlus,
    FileText,
    Bot,
    Mail
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
    createLead,
    createLeadComment,
    deleteLeadComment,
    getLeadDataById,
    updateLead,
    fetchDistinctValues,
    uploadScreenshots,
    deleteScreenshot
} from "../services/crmServices/leadService";
import { fetchAvailableDesks } from "../services/crmServices/deskService";
import { fetchUsers, searchUsers } from "../services/crmServices/userService";
import { getFullName } from "../services/common/util";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";
import { selectFilteredLeads, selectCurrentLeadIndex, setCurrentLeadIndex } from "../store/slices/leadsSlice";
import { useAuth } from "../hooks/useAuth";
import { UpdatePasswordModal } from "../components/leads/UpdatePasswordModal";
import { VoiceCallModal } from "../components/leads/VoiceCallModal";
import { ScheduleCallbackModal } from "../components/calendar/ScheduleCallbackModal";
import { ComposeEmailDialog } from "../components/mail/ComposeEmailDialog";
import { MailActivity } from "../components/mail/MailActivity";
import moment from "moment-timezone";
import { Country } from "country-state-city";
import { searchPositions } from "../services/crmServices/positionService";
import {
    initiateVoisoCall,
    initiateCommpeakCall,
    initiateCoperatoCall,
    initiateSquareTalkCall
} from "../services/voipServices";
import { createLog } from "../services/crmServices/logService";
import { serviceConstants } from "../services/common/constants";

export function CreateLead() {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const filteredLeads = useSelector(selectFilteredLeads);
    const currentLeadIndex = useSelector(selectCurrentLeadIndex);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const leadId = params._id;

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        secondPhone: "",
        status: "new",
        desk: null,
        agent: null,
        floorManager: null,
        source: "",
        affiliate: "",
        country: "",
        countryCode: "", // Add this back
        city: "",
        address: "",
        lastComment: "",
        extra: "",
        balance: "",
        balanceCurrency: "USD"
    });

    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showCallModal, setShowCallModal] = useState(false);
    const [showCallbackModal, setShowCallbackModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [mailTick, setMailTick] = useState(0);
    const [currentLead, setCurrentLead] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [transcriptCall, setTranscriptCall] = useState(null); // AI call transcript modal
    const [accessDenied, setAccessDenied] = useState(false);
    const [loadingLead, setLoadingLead] = useState(false);
    const [screenshots, setScreenshots] = useState([]);
    const [uploadingScreenshots, setUploadingScreenshots] = useState(false);
    const [localTime, setLocalTime] = useState({
        time: "",
        location: "Your Local Time",
        timezone: ""
    });

    const countries = Country.getAllCountries().map((country) => ({
        label: country.name,
        value: country.name,
        code: country.isoCode
    }));

    const [desks, setDesks] = useState([]);
    const [agents, setAgents] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState("");
    const [callLoading, setCallLoading] = useState(false);
    const [floorManagers, setFloorManagers] = useState([]);
    const [isCustomSource, setIsCustomSource] = useState(false);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);

    // Fetch lead data if editing
    useEffect(() => {
        if (!user.permissions.leads.update && leadId) {
            navigate("/leads");
        }
        initLead();

        // Find and set the current lead index in filtered leads if editing
        if (leadId && filteredLeads.length > 0) {
            const leadIndex = filteredLeads.findIndex((lead) => lead._id === leadId);
            if (leadIndex !== -1) {
                dispatch(setCurrentLeadIndex(leadIndex));
            }
        }
    }, [leadId, filteredLeads]);

    const initLead = () => {
        if (leadId) {
            setLoadingLead(true);
            setAccessDenied(false);
            getLeadDataById(leadId).then((res) => {
                if (res.data?._id) {
                    const lead = res.data;
                    setCurrentLead(lead);
                    setLoadingLead(false);
                    setFormData((prev) => ({
                        ...prev,
                        firstName: lead.firstName || "",
                        lastName: lead.lastName || "",
                        email: lead.email || "",
                        phone: lead.phone || "",
                        secondPhone: lead.secondPhone || "",
                        status: lead.status || "new",
                        desk: lead.desk?._id || null,
                        agent: lead.agent?._id || null,
                        floorManager:
                            (lead.floorManager && (lead.floorManager._id || lead.floorManager)) ||
                            (lead.desk?.floorManager && (lead.desk.floorManager._id || lead.desk.floorManager)) ||
                            null,
                        source: lead.source || "",
                        affiliate: lead.affiliate || "",
                        country: lead.country || "",
                        countryCode: lead.country
                            ? countries.find((country) => country.value === lead.country)?.code
                            : null,
                        city: lead.city || "",
                        address: lead.address || "",
                        lastComment: lead.lastComment || "",
                        extra: lead.extra || "",
                        balance: lead.balance || "",
                        balanceCurrency: lead.balanceCurrency || "USD"
                    }));
                    if (lead.comments && lead.comments.length) {
                        // Map comments to ensure they have id field (from _id) and proper structure
                        const mappedComments = lead.comments.map(comment => ({
                            ...comment,
                            id: comment._id || comment.id || Math.random().toString(36).substr(2, 9),
                            isSystem: comment.isSystem || false
                        }));
                        setComments(mappedComments);
                    } else {
                        setComments([]);
                    }
                    
                    // Load screenshots
                    if (lead.screenshots && lead.screenshots.length) {
                        setScreenshots(lead.screenshots);
                    } else {
                        setScreenshots([]);
                    }

                    // if (lead.desk) {

                    // }
                    fetchUsers().then((res) => {
                        if (res?.data?.length) {
                            if (lead.desk) {
                                const filteredAgents = res.data.filter((user) => user.agentDesk?._id === lead.desk._id);
                                setAgents(filteredAgents);
                            } else if (lead.agent) {
                                const filteredAgents = res.data.filter((user) => user._id === lead.agent._id);
                                setAgents(filteredAgents);
                            }
                        } else {
                            setAgents([]);
                        }
                    });
                } else {
                    setLoadingLead(false);
                }
            }).catch((error) => {
                // Handle network errors or other errors
                setLoadingLead(false);
                if (error.response?.status === 403) {
                    // Access denied - lead is no longer assigned to this user
                    setAccessDenied(true);
                    const errorMessage = error.response?.data?.message || error.response?.data?.data?.message || "You no longer have access to this lead. It may have been reassigned.";
                    toast.error(errorMessage);
                } else if (error.response?.status === 404) {
                    setAccessDenied(true);
                    toast.error("Lead not found");
                } else {
                    // For other errors, still show access denied if we're trying to edit a lead
                    if (leadId) {
                        setAccessDenied(true);
                    }
                    toast.error(error.response?.data?.message || "Failed to load lead data");
                }
            });
        }
        fetchAvailableDesks().then((res) => {
            if (res?.data?.length) {
                setDesks(res.data);
            }
        });
        fetchUsers().then((res) => {
            setUsers(res.data);
        });
    };

    // Load floor managers for assignment dropdown
    useEffect(() => {
        const loadFloorManagers = async () => {
            try {
                const positionsRes = await searchPositions({ role: "floorManager" });
                const positions = positionsRes?.data || [];
                if (!positions.length) {
                    setFloorManagers([]);
                    return;
                }
                const positionIds = positions.map((pos) => pos._id);
                const usersRes = await searchUsers({ position: { $in: positionIds } });
                setFloorManagers(usersRes?.data || []);
            } catch (error) {
                console.error("Error loading floor managers:", error);
                setFloorManagers([]);
            }
        };

        loadFloorManagers();
    }, []);

    // Load distinct sources for source dropdown
    useEffect(() => {
        const loadSources = async () => {
            try {
                const res = await fetchDistinctValues("source");
                if (res?.data) {
                    setSourceOptions(
                        res.data.map((source) => ({
                            label: source,
                            value: source
                        }))
                    );
                }
            } catch (error) {
                console.error("Error fetching sources for create lead:", error);
            }
        };

        loadSources();
    }, []);

    // Enhanced timezone lookup - return string instead of object
    const getTimezoneByCountry = (countryName) => {
        const selectedCountry = countries.find((c) => c.value === countryName);
        return selectedCountry?.timezone?.zoneName || "UTC";
        // return "asdf";
    };

    // Get timezone for specific city using coordinates
    const getTimezoneByCity = (countryCode, cityName) => {
        if (!countryCode || !cityName) return getTimezoneByCountry(formData.country);

        // Check if the city name matches any timezone
        const cityTimezone = moment.tz
            .names()
            .find((tz) => tz.toLowerCase().includes(cityName.toLowerCase().replace(/\s+/g, "_")));

        if (cityTimezone) {
            return cityTimezone;
        }

        return getTimezoneByCountry(formData.country);
    };

    // Update local time - show user's local time by default
    useEffect(() => {
        const updateLocalTime = () => {
            let timezone;
            let locationLabel;

            if (formData.country) {
                // Use lead's location if country is selected
                if (formData.city && formData.countryCode) {
                    timezone = getTimezoneByCity(formData.countryCode, formData.city);
                    locationLabel = `${formData.city}, ${formData.country}`;
                } else {
                    timezone = getTimezoneByCountry(formData.country);
                    locationLabel = formData.country;
                }
            } else {
                // Use user's local timezone as default
                timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                locationLabel = "Your Local Time";
            }

            const now = new Date();
            const localDateTime = new Intl.DateTimeFormat("en-US", {
                timeZone: timezone,
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
                weekday: "short"
            }).format(now);

            // setLocalTime(localDateTime);

            // Store location label for display
            setLocalTime(() => {
                const timeData = {
                    time: localDateTime,
                    location: locationLabel,
                    timezone: timezone
                };
                return timeData;
            });
        };

        updateLocalTime();
        const interval = setInterval(updateLocalTime, 1000);
        return () => clearInterval(interval);
    }, [formData.country, formData.city, formData.countryCode]);

    const handleInsertConversionTemplate = () => {
        const balanceDisplay = formData.balance ? `${formData.balanceCurrency || 'USD'} ${formData.balance}` : '';
        const template = `Conversion agent name: ${user.firstName} ${user.lastName}
Calling from: 
-
name: ${formData.firstName || ''} ${formData.lastName || ''}
mail: ${formData.email || ''}
phone: ${formData.phone || ''}
Country: ${formData.country || ''}
-
screenshare type:
-
Active Banks: 
balance: ${balanceDisplay}
Active Exchanges: 
-
Recovery Experience:

Current Situation:
-
specific details of the Call:
-
last transaction in crypto(Reason):

rec amount:`;
        setNewComment(template);
    };

    const handleScreenshotUpload = async (event) => {
        try {
            const files = Array.from(event.target.files || []);
            if (!files.length) return;

            // Validate files
            const validFiles = files.filter(file => {
                const isImage = file.type.startsWith('image/');
                const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
                if (!isImage) {
                    toast.error(`${file.name} is not an image file`);
                    return false;
                }
                if (!isValidSize) {
                    toast.error(`${file.name} exceeds 10MB limit`);
                    return false;
                }
                return true;
            });

            if (!validFiles.length) {
                event.target.value = null;
                return;
            }

            setUploadingScreenshots(true);
            
            const response = await uploadScreenshots(leadId, validFiles);
            
            if (response?.status === 200 || response?.data?.success) {
                toast.success(response.data?.message || "Screenshots uploaded successfully");
                // Refresh lead data to get updated screenshots
                const res = await getLeadDataById(leadId);
                if (res?.data?.screenshots) {
                    setScreenshots(res.data.screenshots);
                }
            } else {
                toast.error(response?.data?.message || response?.message || "Failed to upload screenshots");
            }
        } catch (error) {
            console.error("Error uploading screenshots:", error);
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to upload screenshots. Please try again.";
            toast.error(errorMessage);
        } finally {
            setUploadingScreenshots(false);
            // Reset file input
            if (event.target) {
                event.target.value = null;
            }
        }
    };

    const handleDeleteScreenshot = async (screenshotId) => {
        if (!window.confirm("Are you sure you want to delete this screenshot?")) {
            return;
        }

        try {
            const response = await deleteScreenshot(leadId, screenshotId);
            if (response?.status === 200) {
                toast.success("Screenshot deleted successfully");
                setScreenshots(prev => prev.filter(s => s._id !== screenshotId));
            } else {
                toast.error(response?.message || "Failed to delete screenshot");
            }
        } catch (error) {
            console.error("Error deleting screenshot:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to delete screenshot");
        }
    };

    const handleAddComment = () => {
        if (newComment.trim()) {
            const comment = {
                leadId: leadId,
                text: newComment.trim(),
                author: user._id,
                createdAt: moment().tz("America/New_York").toDate()
            };

            createLeadComment(comment).then((response) => {
                if (response?.status === 200) {
                    // Use the comment from response if available, otherwise use local comment
                    const savedComment = response.data?.lead?.comments?.[response.data.lead.comments.length - 1] || comment;
                    setComments((prev) => [savedComment, ...prev]);
                    setNewComment("");
                    toast.success("Comment added successfully");
                } else if (response.data?.RESULT === "ERROR") {
                    toast.error(response.data.message);
                } else {
                    toast.error(response.message || "Failed to add comment");
                }
            }).catch((error) => {
                console.error("Error adding comment:", error);
                toast.error(error.response?.data?.message || error.message || "Failed to add comment. Please try again.");
            });
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm("Are you sure you want to delete this comment?")) {
            return;
        }

        try {
            const response = await deleteLeadComment({
                leadId: leadId,
                commentId: commentId
            });

            if (response?.status === 200) {
                // Remove comment from local state
                setComments((prev) => prev.filter((comment) => {
                    const id = comment._id || comment.id;
                    return id !== commentId;
                }));
                toast.success("Comment deleted successfully");
                
                // Refresh lead data to get updated comments
                if (leadId) {
                    const res = await getLeadDataById(leadId);
                    if (res.data?._id) {
                        setCurrentLead(res.data);
                        if (res.data.comments && res.data.comments.length) {
                            const mappedComments = res.data.comments.map(comment => ({
                                ...comment,
                                id: comment._id || comment.id || Math.random().toString(36).substr(2, 9),
                                isSystem: comment.isSystem || false
                            }));
                            setComments(mappedComments);
                        } else {
                            setComments([]);
                        }
                    }
                }
            } else {
                toast.error(response.message || "Failed to delete comment");
            }
        } catch (error) {
            console.error("Error deleting comment:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to delete comment. Please try again.");
        }
    };

    const handleDeleteLastComment = async () => {
        if (!window.confirm("Are you sure you want to delete this comment?")) {
            return;
        }

        if (!leadId) {
            toast.error("Cannot delete comment: Lead ID is missing");
            return;
        }

        try {
            const response = await updateLead({ 
                _id: leadId, 
                lastComment: "" 
            });

            if (response?.status === 200) {
                toast.success("Comment deleted successfully");
                
                // Refresh lead data to get updated lastComment
                const res = await getLeadDataById(leadId);
                if (res.data?._id) {
                    setCurrentLead(res.data);
                }
            } else {
                toast.error(response.message || "Failed to delete comment");
            }
        } catch (error) {
            console.error("Error deleting last comment:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to delete comment. Please try again.");
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleAddComment();
        }
    };

    // Add this validation function
    const validatePhoneNumbers = () => {
        const phone = formData.phone?.trim();
        const secondPhone = formData.secondPhone?.trim();

        // If both phones are provided and they are the same
        if (phone && secondPhone && phone === secondPhone) {
            return {
                isValid: false,
                message: "Phone number and Second Phone cannot be the same"
            };
        }

        return { isValid: true };
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate that either firstName or lastName is provided
        const hasFirstName = formData.firstName && formData.firstName.trim() !== "";
        const hasLastName = formData.lastName && formData.lastName.trim() !== "";
        if (!hasFirstName && !hasLastName) {
            toast.error("First Name or Last Name is required");
            return;
        }

        // Validate phone numbers
        const phoneValidation = validatePhoneNumbers();
        if (!phoneValidation.isValid) {
            toast.error(phoneValidation.message);
            return;
        }

        if (leadId) {
            updateLead({ _id: leadId, ...formData }).then(async (response) => {
                if (response?.status === 200) {
                    toast.success("Lead updated successfully!");
                    // Refresh lead data to show updated values instead of navigating away
                    const res = await getLeadDataById(leadId);
                    if (res.data?._id) {
                        setCurrentLead(res.data);
                        // Update form data with the latest values from server
                        setFormData((prev) => ({
                            ...prev,
                            firstName: res.data.firstName || "",
                            lastName: res.data.lastName || "",
                            email: res.data.email || "",
                            phone: res.data.phone || "",
                            secondPhone: res.data.secondPhone || "",
                            status: res.data.status || "new",
                            desk: res.data.desk?._id || null,
                            agent: res.data.agent?._id || null,
                            floorManager:
                                (res.data.floorManager && (res.data.floorManager._id || res.data.floorManager)) ||
                                (res.data.desk?.floorManager && (res.data.desk.floorManager._id || res.data.desk.floorManager)) ||
                                null,
                            source: res.data.source || "",
                            affiliate: res.data.affiliate || "",
                            country: res.data.country || "",
                            countryCode: res.data.country
                                ? countries.find((country) => country.value === res.data.country)?.code
                                : null,
                            city: res.data.city || "",
                            address: res.data.address || "",
                            lastComment: res.data.lastComment || "",
                            extra: res.data.extra || "",
                            balance: res.data.balance || "",
                            balanceCurrency: res.data.balanceCurrency || "USD"
                        }));
                    }
                } else {
                    if (response.data?.RESULT === "ERROR") {
                        toast.error(response.data.message);
                    } else {
                        toast.error(response.message || "Failed to update lead");
                    }
                }
            }).catch((error) => {
                console.error("Error updating lead:", error);
                toast.error(error.response?.data?.message || error.message || "Failed to update lead");
            });
        } else {
            // When creating a new lead, copy extra field content to lastComment and generate default password
            // Generate a default password (email + timestamp if email exists, otherwise random string)
            const defaultPassword = formData.email 
                ? `${formData.email}_${Date.now()}` 
                : `lead_${Date.now()}_${Math.random().toString(36).substring(7)}`;
            
            const leadDataToCreate = {
                ...formData,
                password: defaultPassword,
                lastComment: formData.extra ? formData.extra : formData.lastComment || ""
            };
            
            createLead(leadDataToCreate).then((response) => {
                if (response?.status === 200) {
                    toast.success("Lead created successfully!");
                    navigate("/leads");
                } else {
                    if (response.data?.RESULT === "ERROR") {
                        toast.error(response.data.message);
                    } else {
                        toast.error(response.message || "Failed to create lead");
                    }
                }
            });
        }
    };

    const handleDeskChange = (value) => {
        if (value !== formData.desk) {
            // Immediately clear agents and show loading state for better UX
            setAgents([]);
            setAgentsLoading(true);
            fetchUsers().then((res) => {
                if (res?.data?.length) {
                    const filteredAgents = res.data.filter((user) => user.agentDesk?._id === value);
                    setAgents(filteredAgents);
                    setAgentsLoading(false);
                } else {
                    setAgents([]);
                    setAgentsLoading(false);
                }
            }).catch((error) => {
                console.error("Error fetching agents for desk:", error);
                setAgents([]);
                setAgentsLoading(false);
            });
            setFormData((prev) => ({ ...prev, desk: value, agent: null }));
        }
    };

    const handlePasswordUpdateSuccess = () => {
        setShowPasswordModal(false);
        toast.success("Password updated successfully!");
    };

    const handleBack = () => {
        navigate("/leads");
    };

    const handlePrevious = () => {
        if (currentLeadIndex > 0) {
            const previousLead = filteredLeads[currentLeadIndex - 1];
            navigate(`/leads/edit/${previousLead._id}`);
        }
    };

    const handleNext = () => {
        if (currentLeadIndex < filteredLeads.length - 1) {
            const nextLead = filteredLeads[currentLeadIndex + 1];
            navigate(`/leads/edit/${nextLead._id}`);
        }
    };

    const handleCountryChange = (value) => {
        const selectedCountry = countries.find((country) => country.value === value);
        setFormData((prev) => ({
            ...prev,
            country: value,
            countryCode: selectedCountry?.code || "",
            city: "" // Reset city when country changes
        }));
    };

    const handleCityChange = (e) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, city: value }));
    };

    // Check if phone number is missing
    const isPhoneMissing =
        (!formData.phone || formData.phone.trim() === "") &&
        (!formData.secondPhone || formData.secondPhone.trim() === "");

    // Add real-time validation with visual feedback
    const [phoneError, setPhoneError] = useState("");

    // Desks filtered by selected floor manager (if any)
    const filteredDesks = formData.floorManager
        ? desks.filter((desk) => {
              const fmId = desk.floorManager?._id || desk.floorManager;
              if (!fmId) return false;
              const fmIdStr = fmId.toString ? fmId.toString() : String(fmId);
              const selectedIdStr = formData.floorManager.toString
                  ? formData.floorManager.toString()
                  : String(formData.floorManager);
              return fmIdStr === selectedIdStr;
          })
        : desks;

    const canEditSource =
        user.position?.role === "admin" || user.permissions?.editLeadFields?.source;

    // If existing source value is not in dropdown options (e.g., old custom value),
    // default to custom mode so it can be edited as free text.
    useEffect(() => {
        if (formData.source && sourceOptions.length > 0) {
            const exists = sourceOptions.some((opt) => opt.value === formData.source);
            if (!exists) {
                setIsCustomSource(true);
            }
        }
    }, [formData.source, sourceOptions]);

    // Add useEffect to validate phones when they change
    useEffect(() => {
        const phone = formData.phone?.trim();
        const secondPhone = formData.secondPhone?.trim();

        if (phone && secondPhone && phone === secondPhone) {
            setPhoneError("Phone numbers cannot be the same");
        } else {
            setPhoneError("");
        }
    }, [formData.phone, formData.secondPhone]);

    // Show access denied message if user doesn't have access
    if (accessDenied && leadId) {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-8">
                <Card className="max-w-2xl mx-auto">
                    <CardContent className="p-8 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="bg-red-100 rounded-full p-4">
                                <Lock className="h-12 w-12 text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
                            <p className="text-gray-600 max-w-md">
                                You no longer have access to this lead. It may have been reassigned to another agent or removed from your access.
                            </p>
                            <div className="flex space-x-4 mt-6">
                                <Button onClick={() => navigate("/leads")} className="bg-blue-600 hover:bg-blue-700">
                                    Go to Leads
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Show loading state while checking access
    if (loadingLead && leadId) {
        return (
            <div className="max-w-[1600px] mx-auto px-4 py-8">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-500 mx-auto mb-3"></div>
                        <p className="text-sm text-gray-600 font-medium">Loading lead...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-[1600px] mx-auto px-4">
            <div className="mb-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">{leadId ? "Edit" : "Create"} Lead</h1>
                        {leadId && currentLead && (
                            <p className="text-sm text-gray-600 mt-0.5">
                                {currentLead.firstName} {currentLead.lastName} • {currentLead.email}
                            </p>
                        )}
                    </div>

                    {/* Action buttons for editing */}
                    {leadId && currentLead && (
                        <div className="flex gap-2">
                            {user.position.role === "admin" && (
                                <button
                                    onClick={() => setShowPasswordModal(true)}
                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-300 hover:bg-purple-100 rounded-md transition-colors"
                                >
                                    <Key className="h-4 w-4 mr-1.5" />
                                    Password
                                </button>
                            )}
                            {/* Call Button - Only show for existing leads with phone numbers */}
                            {leadId && currentLead && (currentLead.phone || currentLead.secondPhone) && (
                                <button
                                    onClick={() => setShowCallModal(true)}
                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                                >
                                    <Phone className="h-4 w-4 mr-1.5" />
                                    Call
                                </button>
                            )}
                            {/* Schedule Callback - Only show for existing leads */}
                            {leadId && currentLead && (
                                <button
                                    onClick={() => setShowCallbackModal(true)}
                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 hover:bg-blue-100 rounded-md transition-colors"
                                    title="Schedule a callback on your calendar"
                                >
                                    <CalendarPlus className="h-4 w-4 mr-1.5" />
                                    Schedule Callback
                                </button>
                            )}
                            {leadId && currentLead && currentLead.email && (
                                <button
                                    onClick={() => setShowEmailModal(true)}
                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                                    title="Send email from your mailbox"
                                >
                                    <Mail className="h-4 w-4 mr-1.5" />
                                    Send Email
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Navigation controls - Only show in edit mode */}
                {leadId && filteredLeads.length > 1 && (
                    <div className="flex items-center justify-between bg-gray-50 border border-gray-200 p-3 rounded-md">
                        <button
                            onClick={handlePrevious}
                            disabled={currentLeadIndex <= 0}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50:bg-gray-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                        </button>

                        <span className="text-xs text-gray-600 font-medium">
                            {currentLeadIndex + 1} of {filteredLeads.length}
                        </span>

                        <button
                            onClick={handleNext}
                            disabled={currentLeadIndex >= filteredLeads.length - 1}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50:bg-gray-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                        </button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Main Form - Left side (2/3 width) */}
                <div className="lg:col-span-2">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <Card className="border-gray-200 shadow-sm">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Basic Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="firstName" className="text-xs text-gray-600 mb-1">First Name <span className="text-gray-400 text-[10px]">(required)</span></Label>
                                        <Input
                                            id="firstName"
                                            value={formData.firstName}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, firstName: e.target.value }))
                                            }
                                            disabled={user.permissions.editLeadFields?.firstName ? false : true}
                                            className="h-9 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="lastName" className="text-xs text-gray-600 mb-1">Last Name</Label>
                                        <Input
                                            id="lastName"
                                            value={formData.lastName}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, lastName: e.target.value }))
                                            }
                                            disabled={user.permissions.editLeadFields?.lastName ? false : true}
                                            className="h-9 text-sm"
                                        />
                                    </div>
                                </div>
                                {user.permissions.leads.viewEmail && (
                                    <div>
                                        <Label htmlFor="email" className="text-xs text-gray-600 mb-1">Email Address <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, email: e.target.value }))
                                            }
                                            disabled={user.permissions.editLeadFields?.email ? false : true}
                                            required
                                            className="h-9 text-sm"
                                        />
                                    </div>
                                )}
                                {user.permissions.leads.viewPhone && (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <Label htmlFor="phone" className="text-xs text-gray-600 mb-1">Phone Number</Label>
                                                <Input
                                                    id="phone"
                                                    value={formData.phone}
                                                    onChange={(e) =>
                                                        setFormData((prev) => ({ ...prev, phone: e.target.value }))
                                                    }
                                                    disabled={user.permissions.editLeadFields?.phone ? false : true}
                                                    className={`h-9 text-sm ${phoneError ? "border-red-500" : ""}`}
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="secondPhone" className="text-xs text-gray-600 mb-1">Second Phone</Label>
                                                <Input
                                                    id="secondPhone"
                                                    value={formData.secondPhone}
                                                    onChange={(e) =>
                                                        setFormData((prev) => ({ ...prev, secondPhone: e.target.value }))
                                                    }
                                                    disabled={user.permissions.editLeadFields?.secondPhone ? false : true}
                                                    className={`h-9 text-sm ${phoneError ? "border-red-500" : ""}`}
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Phone number warning */}
                                        {isPhoneMissing && !phoneError && (
                                            <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                                                <PhoneOff className="h-3.5 w-3.5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                                <p className="text-yellow-700">Voice calling disabled - no phone number</p>
                                            </div>
                                        )}
                                        
                                        {/* Show validation error */}
                                        {phoneError && (
                                            <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs">
                                                <AlertTriangle className="h-3.5 w-3.5 text-red-600 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <p className="font-semibold text-red-800">Error</p>
                                                    <p className="text-red-700">{phoneError}</p>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="country" className="text-xs text-gray-600 mb-1">Country</Label>
                                        <Select
                                            options={countries}
                                            value={formData.country}
                                            onChange={handleCountryChange}
                                            placeholder="Select country"
                                            searchable
                                            disabled={user.permissions.editLeadFields?.country ? false : true}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="status" className="text-xs text-gray-600 mb-1">Status</Label>
                                        <Select
                                            options={leadStatuses}
                                            value={formData.status}
                                            onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
                                            placeholder="Select status"
                                            searchable
                                            disabled={user.permissions.editLeadFields?.status ? false : true}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <Label htmlFor="balance" className="text-xs text-gray-600 mb-1">Balance (Amount in Bank)</Label>
                                        <div className="flex gap-2">
                                            <Select
                                                options={[
                                                    { label: "USD ($)", value: "USD" },
                                                    { label: "EUR (€)", value: "EUR" },
                                                    { label: "CAD ($)", value: "CAD" },
                                                    { label: "GBP (£)", value: "GBP" },
                                                    { label: "AUD ($)", value: "AUD" }
                                                ]}
                                                value={formData.balanceCurrency || "USD"}
                                                onChange={(value) => setFormData((prev) => ({ ...prev, balanceCurrency: value }))}
                                                className="w-32"
                                            />
                                            <Input
                                                id="balance"
                                                value={formData.balance}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, balance: e.target.value }))}
                                                placeholder="e.g., 5000 or 3000"
                                                className="h-9 text-sm flex-1"
                                                type="number"
                                                step="0.01"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-gray-200 shadow-sm">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Assignment & Source</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {/* Show desk/agent fields only for new leads */}
                                {(!leadId || user.position.role !== "agent") && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <Label htmlFor="floorManager" className="text-xs text-gray-600">Floor Manager</Label>
                                                {formData.floorManager && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                floorManager: null,
                                                                desk: null,
                                                                agent: null
                                                            }));
                                                            setAgents([]);
                                                        }}
                                                        className="h-5 px-1.5 text-[10px] text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>
                                            <Select
                                                options={floorManagers.map((fm) => ({
                                                    label: getFullName(fm),
                                                    value: fm._id
                                                }))}
                                                value={formData.floorManager}
                                                onChange={(value) => {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        floorManager: value,
                                                        desk: null,
                                                        agent: null
                                                    }));
                                                    setAgents([]);
                                                }}
                                                placeholder="Select floor manager"
                                                searchable
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="desk" className="text-xs text-gray-600 mb-1">Desk</Label>
                                            <Select
                                                options={filteredDesks.map((desk) => ({
                                                    label: desk.name,
                                                    value: desk._id
                                                }))}
                                                value={formData.desk}
                                                onChange={(value) => handleDeskChange(value)}
                                                placeholder="Select desk"
                                                searchable
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="agent" className="text-xs text-gray-600 mb-1">Agent</Label>
                                            <Select
                                                options={agents.map((agent) => ({
                                                    label: getFullName(agent),
                                                    value: agent._id
                                                }))}
                                                value={formData.agent}
                                                onChange={(value) =>
                                                    setFormData((prev) => ({ ...prev, agent: value }))
                                                }
                                                placeholder={agentsLoading ? "Loading agents..." : "Select agent"}
                                                searchable
                                                disabled={agentsLoading || !formData.desk}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Show current assignment for editing leads */}
                                {leadId && currentLead && user.position.role === "agent" && (
                                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-md">
                                        <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-2">Assignment</h4>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>
                                                <span className="text-gray-500">Desk:</span>
                                                <span className="ml-1.5 font-medium text-gray-900">
                                                    {currentLead.desk?.name || "Not assigned"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Agent:</span>
                                                <span className="ml-1.5 font-medium text-gray-900">
                                                    {currentLead.agent
                                                        ? getFullName(currentLead.agent)
                                                        : "Not assigned"}
                                                </span>
                                            </div>
                                            <div className="col-span-2">
                                                <span className="text-gray-500">Manager:</span>
                                                <span className="ml-1.5 font-medium text-gray-900">
                                                    {currentLead.desk?.manager
                                                        ? getFullName(currentLead.desk.manager)
                                                        : "Not assigned"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="source" className="text-xs text-gray-600 mb-1">Source</Label>
                                        {canEditSource && !isCustomSource && (
                                            <Select
                                                options={[
                                                    ...sourceOptions,
                                                    { label: "Add new source...", value: "__custom__" }
                                                ]}
                                                value={
                                                    sourceOptions.some(
                                                        (opt) => opt.value === formData.source
                                                    )
                                                        ? formData.source
                                                        : ""
                                                }
                                                onChange={(value) => {
                                                    if (value === "__custom__") {
                                                        setIsCustomSource(true);
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            source: ""
                                                        }));
                                                    } else {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            source: value
                                                        }));
                                                    }
                                                }}
                                                placeholder="Select source"
                                                searchable
                                                disabled={!canEditSource}
                                            />
                                        )}
                                        {(!canEditSource || isCustomSource) && (
                                            <div className="space-y-1">
                                                <Input
                                                    id="source"
                                                    value={formData.source}
                                                    onChange={(e) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            source: e.target.value
                                                        }))
                                                    }
                                                    disabled={!canEditSource}
                                                    placeholder="Enter source"
                                                    className="h-9 text-sm"
                                                />
                                                {canEditSource && sourceOptions.length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsCustomSource(false)}
                                                        className="text-xs text-gray-600 hover:text-gray-900 transition-colors"
                                                    >
                                                        Use existing source
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="affiliate" className="text-xs text-gray-600 mb-1">Affiliate</Label>
                                        <Input
                                            id="affiliate"
                                            value={formData.affiliate}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, affiliate: e.target.value }))
                                            }
                                            className="h-9 text-sm"
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-gray-200 shadow-sm">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Notes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Label htmlFor="extra" className="text-xs text-gray-600 mb-1">Additional Notes</Label>
                                <Textarea
                                    id="extra"
                                    value={formData.extra}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, extra: e.target.value }))}
                                    rows={2}
                                    disabled={!user.permissions?.editLeadFields?.extra && user.position.role !== "admin"}
                                    className="text-sm resize-none"
                                />
                            </CardContent>
                        </Card>

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="submit"
                                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                            >
                                {leadId ? "Update" : "Create"} Lead
                            </button>
                            <button
                                type="button"
                                onClick={handleBack}
                                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>

                {/* Right Sidebar - Enhanced Local Time & Comments */}
                <div className="lg:col-span-1 space-y-4">
                    {/* Enhanced Local Time Card - Always show */}
                    <Card className="border-gray-200 shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center text-sm font-semibold text-gray-900 uppercase tracking-wide">
                                <Clock className="h-4 w-4 text-gray-600 mr-2" />
                                Local Time
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-2">
                            <div className="text-center bg-gray-50 rounded p-3">
                                <div className="text-lg font-mono font-semibold text-gray-900 mb-1">
                                    {typeof localTime === "object" ? localTime.time : localTime}
                                </div>
                                <div className="text-[10px] text-gray-500 uppercase tracking-wider">
                                    {localTime.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {leadId && (
                        <MailActivity
                            key={`${leadId}-${mailTick}`}
                            leadId={leadId}
                            onOpen={() => setShowEmailModal(true)}
                        />
                    )}

                    {/* Comments Card - Only show for existing leads */}
                    {leadId && (
                        <Card className="h-[600px] flex flex-col overflow-hidden border-gray-200 shadow-sm">
                            <CardHeader className="pb-2 flex-shrink-0">
                                <CardTitle className="flex items-center justify-between text-sm font-semibold text-gray-900 uppercase tracking-wide">
                                    <div className="flex items-center">
                                        <MessageCircle className="h-4 w-4 mr-2 text-gray-600" />
                                        Comments
                                    </div>
                                    <span className="text-xs font-normal text-gray-500">
                                        ({comments.length})
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="flex-1 flex flex-col p-0 min-h-0 overflow-hidden">
                                {/* Add Comment Section */}
                                <div className="p-3 border-b flex-shrink-0 bg-gray-50">
                                    <div className="flex gap-2 mb-2">
                                        <button
                                            type="button"
                                            onClick={handleInsertConversionTemplate}
                                            className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-300 hover:bg-blue-100 rounded-md transition-colors"
                                            title="Insert conversion template"
                                        >
                                            📋 Conversion Template
                                        </button>
                                        <label className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-300 hover:bg-green-100 rounded-md transition-colors cursor-pointer">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={handleScreenshotUpload}
                                                className="hidden"
                                                disabled={uploadingScreenshots}
                                            />
                                            <ImagePlus className="h-3.5 w-3.5 inline mr-1" />
                                            {uploadingScreenshots ? "Uploading..." : "Upload Screenshots"}
                                        </label>
                                    </div>
                                    <div className="flex gap-2">
                                        <Textarea
                                            placeholder="Add a comment..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            rows={6}
                                            className="flex-1 resize-y text-sm min-h-[100px]"
                                        />
                                        <button
                                            onClick={handleAddComment}
                                            disabled={!newComment.trim()}
                                            className="self-end p-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Send className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-gray-500 mt-1">
                                        Press Enter to send
                                    </p>
                                </div>

                                {/* Comments List */}
                                <div className="flex-1 overflow-y-auto p-3 min-h-0">
                                    {comments.length === 0 && !currentLead?.lastComment ? (
                                        <div className="text-center text-gray-400 py-12">
                                            <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-30" />
                                            <p className="text-sm">No comments yet</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {/* Display lastComment only if it's NOT already in the comments array (to avoid duplicates) */}
                                            {currentLead?.lastComment && !comments.some(comment => comment.text === currentLead.lastComment) && (
                                                <div className="p-2.5 rounded bg-yellow-50 border-l-2 border-yellow-400 break-words">
                                                    <div className="flex justify-between items-start mb-1.5 gap-2">
                                                        <span className="text-xs font-semibold text-yellow-800 uppercase">
                                                            Latest
                                                        </span>
                                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                                            <span className="text-[10px] text-gray-500 whitespace-nowrap">
                                                                {currentLead.updatedAt 
                                                                    ? moment(currentLead.updatedAt).local().format("MMM DD, HH:mm")
                                                                    : ""}
                                                            </span>
                                                            {user.position.role === "admin" && (
                                                                <button
                                                                    onClick={handleDeleteLastComment}
                                                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                    title="Delete comment"
                                                                >
                                                                    <Trash2 className="h-3 w-3" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <p className="text-xs text-gray-700 whitespace-pre-wrap break-words">
                                                        {currentLead.lastComment}
                                                    </p>
                                                </div>
                                            )}
                                            {comments.map((comment) => {
                                                const commentId = comment._id || comment.id;
                                                const ai = comment.aiCall;
                                                const isAi = !!ai;
                                                const hasCallDetail = isAi && (ai.transcript || ai.recordingUrl);
                                                return (
                                                    <div
                                                        key={comment.id}
                                                        className={`p-2.5 rounded break-words ${
                                                            isAi
                                                                ? "bg-indigo-50 border-l-2 border-indigo-400"
                                                                : comment.isSystem
                                                                    ? "bg-blue-50 border-l-2 border-blue-400"
                                                                    : "bg-gray-50 border-l-2 border-gray-300"
                                                        }`}
                                                    >
                                                        <div className="flex justify-between items-start mb-1.5 gap-2">
                                                            <span
                                                                className={`text-xs font-semibold break-words flex items-center gap-1 ${
                                                                    isAi ? "text-indigo-700" : comment.isSystem ? "text-blue-700" : "text-gray-900"
                                                                }`}
                                                            >
                                                                {isAi && <Bot className="h-3 w-3" />}
                                                                {comment.affiliateName 
                                                                    ? comment.affiliateName
                                                                    : users.length
                                                                        ? getFullName(
                                                                              users.find((u) => u._id === comment.author)
                                                                          ) || "Unknown"
                                                                        : ""}
                                                            </span>
                                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                                <span className="text-[10px] text-gray-500 whitespace-nowrap">
                                                                    {moment(comment.createdAt)
                                                                        .local()
                                                                        .format("MMM DD, HH:mm")}
                                                                </span>
                                                                {user.position.role === "admin" && (
                                                                    <button
                                                                        onClick={() => handleDeleteComment(commentId)}
                                                                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                        title="Delete comment"
                                                                    >
                                                                        <Trash2 className="h-3 w-3" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p className="text-xs text-gray-700 whitespace-pre-wrap break-words">
                                                            {comment.text}
                                                        </p>
                                                        {isAi && (
                                                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                                                {typeof ai.durationSeconds === "number" && (
                                                                    <span className="text-[10px] text-indigo-700 bg-indigo-100 rounded px-1.5 py-0.5">
                                                                        {Math.floor(ai.durationSeconds / 60)}m {ai.durationSeconds % 60}s
                                                                    </span>
                                                                )}
                                                                {ai.sentiment && (
                                                                    <span className="text-[10px] text-indigo-700 bg-indigo-100 rounded px-1.5 py-0.5">
                                                                        {ai.sentiment}
                                                                    </span>
                                                                )}
                                                                {hasCallDetail && (
                                                                    <button
                                                                        onClick={() => setTranscriptCall({ ...ai, createdAt: comment.createdAt })}
                                                                        className="inline-flex items-center gap-1 text-[10px] text-indigo-700 border border-indigo-300 rounded px-1.5 py-0.5 hover:bg-indigo-100 transition-colors"
                                                                    >
                                                                        <FileText className="h-3 w-3" />
                                                                        View transcript / recording
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Screenshots Card - Only show for existing leads */}
                    {leadId && screenshots.length > 0 && (
                        <Card className="border-gray-200 shadow-sm">
                            <CardHeader className="pb-2 py-2">
                                <CardTitle className="flex items-center justify-between text-xs font-semibold text-gray-900 uppercase tracking-wide">
                                    <div className="flex items-center">
                                        <ImagePlus className="h-3.5 w-3.5 mr-1.5 text-gray-600" />
                                        Screenshots
                                    </div>
                                    <span className="text-xs font-normal text-gray-500">
                                        ({screenshots.length})
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-2 pb-3">
                                <div className="grid grid-cols-3 gap-1.5">
                                    {screenshots.map((screenshot) => (
                                        <div key={screenshot._id} className="relative group">
                                            <a
                                                href={`${serviceConstants.API_HOST_NAME}${screenshot.path}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block aspect-square rounded border border-gray-200 overflow-hidden hover:border-blue-400 transition-colors"
                                            >
                                                <img
                                                    src={`${serviceConstants.API_HOST_NAME}${screenshot.path}`}
                                                    alt={screenshot.originalName}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" text-anchor="middle" fill="gray">No Image</text></svg>';
                                                    }}
                                                />
                                            </a>
                                            <div className="absolute top-0.5 right-0.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <a
                                                    href={`${serviceConstants.API_HOST_NAME}${screenshot.path}`}
                                                    download={screenshot.originalName}
                                                    className="p-0.5 bg-white rounded shadow hover:bg-gray-100"
                                                    title="Download"
                                                >
                                                    <Download className="h-3 w-3 text-gray-700" />
                                                </a>
                                                {user.position.role === "admin" && (
                                                    <button
                                                        onClick={() => handleDeleteScreenshot(screenshot._id)}
                                                        className="p-0.5 bg-white rounded shadow hover:bg-red-50"
                                                        title="Delete"
                                                    >
                                                        <X className="h-3 w-3 text-red-600" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>


            {/* Update Password Modal */}
            <UpdatePasswordModal
                open={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                onSuccess={handlePasswordUpdateSuccess}
                lead={currentLead}
            />

            {/* Schedule Callback Modal */}
            <ComposeEmailDialog
                open={showEmailModal}
                onClose={() => setShowEmailModal(false)}
                lead={currentLead}
                onSent={() => setMailTick((n) => n + 1)}
            />
            <ScheduleCallbackModal
                open={showCallbackModal}
                onClose={() => setShowCallbackModal(false)}
                lead={currentLead}
            />

            {/* Voice Call Modal */}
            <VoiceCallModal
                open={showCallModal}
                onClose={() => {
                    setShowCallModal(false);
                    initLead(); // Refresh lead data after call
                }}
                lead={currentLead}
                onLeadUpdate={(updatedLead) => {
                    // Don't update currentLead here as it causes re-render that closes/reopens modal
                    // The modal will handle the update internally via localLead
                    // We'll refresh currentLead when modal closes via onClose callback
                }}
                onSuccess={() => {
                    setShowCallModal(false);
                    // Refresh lead data after call
                    if (leadId) {
                        getLeadDataById(leadId).then((res) => {
                            if (res.data?._id) {
                                setCurrentLead(res.data);
                                if (res.data.comments && res.data.comments.length) {
                                    setComments(res.data.comments);
                                }
                            }
                        });
                    }
                }}
            />

            {/* AI call transcript / recording modal */}
            <Dialog open={!!transcriptCall} onOpenChange={(v) => !v && setTranscriptCall(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-indigo-600" /> AI Call Details
                        </DialogTitle>
                    </DialogHeader>
                    {transcriptCall && (
                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2 text-xs">
                                {transcriptCall.createdAt && (
                                    <span className="bg-gray-100 rounded px-2 py-1 text-gray-700">
                                        {moment(transcriptCall.createdAt).local().format("MMM DD, YYYY HH:mm")}
                                    </span>
                                )}
                                {typeof transcriptCall.durationSeconds === "number" && (
                                    <span className="bg-gray-100 rounded px-2 py-1 text-gray-700">
                                        {Math.floor(transcriptCall.durationSeconds / 60)}m {transcriptCall.durationSeconds % 60}s
                                    </span>
                                )}
                                {transcriptCall.sentiment && (
                                    <span className="bg-indigo-100 rounded px-2 py-1 text-indigo-700">
                                        {transcriptCall.sentiment}
                                    </span>
                                )}
                            </div>
                            {transcriptCall.recordingUrl && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-700 mb-1">Recording</p>
                                    <audio controls className="w-full" src={transcriptCall.recordingUrl} />
                                </div>
                            )}
                            {transcriptCall.callSummary && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-700 mb-1">Summary</p>
                                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{transcriptCall.callSummary}</p>
                                </div>
                            )}
                            {transcriptCall.transcript ? (
                                <div>
                                    <p className="text-xs font-semibold text-gray-700 mb-1">Transcript</p>
                                    <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-sans max-h-96 overflow-y-auto">{transcriptCall.transcript}</pre>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No transcript available for this call.</p>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
